import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import {
  fetchUserThreads,
  fetchThreadMessages,
  createNewThread,
  postThreadMessage,
  deleteThreadMessage,
  getChannelDeepLink,
} from '../utils/comsHub';
import './ComsEmbedPanel.css';

/**
 * ComsEmbedPanel - Reusable embedded threads widget
 * Shows thread list, full thread view with messages, and compose functionality
 * Used in both header quick panel and account page full view
 * 
 * Props:
 * - channel: 'dev' | 'staff' | 'citizen' (default: 'dev')
 * - compact: boolean - if true, shows preview mode with limited threads; if false, full interface
 * - onOpenInComSapp: optional callback when user clicks "Open in ComSapp"
 * - onRelinkClick: optional callback when user clicks relink button
 */
const ComsEmbedPanel = ({ 
  channel = 'dev', 
  compact = true,
  onOpenInComSapp,
  onRelinkClick 
}) => {
  const { currentUser, comsappLinking, relinkingLoading, relinkComsappAccount } = useContext(AuthContext);

  // UI State
  const [view, setView] = useState('threads');     // 'threads' | 'thread-detail' | 'compose'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [deepLink, setDeepLink] = useState(null);
  const [refreshSignal, setRefreshSignal] = useState(0);

  // Compose state
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composing, setComposing] = useState(false);

  const getEntityId = (entity) => entity?._id || entity?.id || entity?.threadId || entity?.messageId || null;
  const currentUserId = String(currentUser?.id || currentUser?._id || '');

  // Load threads on mount
  useEffect(() => {
    loadThreads();
    loadDeepLink();
  }, [channel, refreshSignal]);

  useEffect(() => {
    const handleLinkingUpdated = () => {
      setRefreshSignal((prev) => prev + 1);
    };

    window.addEventListener('comsapp-linking-updated', handleLinkingUpdated);
    return () => window.removeEventListener('comsapp-linking-updated', handleLinkingUpdated);
  }, []);

  const loadThreads = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchUserThreads(api, channel);
      if (result.success) {
        setThreads(result.threads);
        setView('threads');
      } else {
        setError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDeepLink = async () => {
    try {
      const result = await getChannelDeepLink(api, channel);
      if (result.success) {
        setDeepLink(result.deepLink);
      }
    } catch (err) {
      console.error('Error loading deep link:', err);
    }
  };

  const handleThreadClick = async (thread) => {
    const threadId = getEntityId(thread);
    if (!threadId) {
      setError('Selected thread is missing an id');
      return;
    }

    setSelectedThread(thread);
    setView('thread-detail');
    setMessagesLoading(true);
    setMessages([]);

    try {
      const result = await fetchThreadMessages(api, threadId, channel);
      if (result.success) {
        setMessages(result.messages);
      } else {
        setError(result.error);
      }
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleCreateThreadClick = () => {
    setComposeSubject('');
    setComposeMessage('');
    setView('compose');
  };

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!composeSubject.trim() || !composeMessage.trim()) {
      setError('Thread subject and message are required');
      return;
    }

    setComposing(true);
    setError(null);

    try {
      const result = await createNewThread(api, {
        channel,
        subject: composeSubject,
        type: 'message',
      });

      if (result.success && result.thread) {
        const newThreadId = getEntityId(result.thread);
        if (!newThreadId) {
          setError('Created thread is missing an id');
          return;
        }

        // Post the initial message to the new thread
        const msgResult = await postThreadMessage(api, newThreadId, {
          channel,
          body: composeMessage,
        });

        if (msgResult.success) {
          // Reload threads and go back to list
          await loadThreads();
          setView('threads');
        } else {
          setError(msgResult.error);
        }
      } else {
        setError(result.error);
      }
    } finally {
      setComposing(false);
    }
  };

  const handlePostReply = async (e) => {
    e.preventDefault();
    if (!composeMessage.trim()) {
      setError('Message is required');
      return;
    }

    const selectedThreadId = getEntityId(selectedThread);
    if (!selectedThreadId) {
      setError('Cannot reply: thread id is missing');
      return;
    }

    setComposing(true);
    setError(null);

    try {
      const result = await postThreadMessage(api, selectedThreadId, {
        channel,
        body: composeMessage,
      });

      if (result.success) {
        // Reload messages
        const msgResult = await fetchThreadMessages(api, selectedThreadId, channel);
        if (msgResult.success) {
          setMessages(msgResult.messages);
          setComposeMessage('');
        }
      } else {
        setError(result.error);
      }
    } finally {
      setComposing(false);
    }
  };

  const handleOpenInComSapp = () => {
    if (deepLink) {
      window.open(deepLink, '_blank');
    }
    if (onOpenInComSapp) {
      onOpenInComSapp();
    }
  };

  const handleDeleteMessage = async (messageId) => {
    const selectedThreadId = getEntityId(selectedThread);
    if (!selectedThreadId || !messageId) {
      setError('Cannot delete: missing thread or message id');
      return;
    }

    if (!window.confirm('Delete this message?')) {
      return;
    }

    setError(null);
    const result = await deleteThreadMessage(api, selectedThreadId, messageId, channel);
    if (!result.success) {
      setError(result.error);
      return;
    }

    const msgResult = await fetchThreadMessages(api, selectedThreadId, channel);
    if (msgResult.success) {
      setMessages(msgResult.messages);
    }
  };

  const handleRelink = async () => {
    const result = await relinkComsappAccount();
    if (result.success) {
      // Reload threads to show updated state
      loadThreads();
    }
    if (onRelinkClick) {
      onRelinkClick(result);
    }
  };

  const appearsLinked = comsappLinking?.primary?.syncStatus === 'linked' || Boolean(deepLink);
  const linkingStatusDisplay = appearsLinked
    ? '✓ Linked' 
    : comsappLinking?.primary?.syncError
    ? `⚠ Error: ${comsappLinking.primary.syncError}`
    : '○ Pending';

  // ────────────────────────────────────────────────────────────────────────
  // Compact view (preview for header)
  // ────────────────────────────────────────────────────────────────────────
  if (compact && view === 'threads') {
    return (
      <div className="coms-embed-panel coms-embed-panel--compact">
        <div className="coms-embed-header">
          <h4>Messages</h4>
          <div className="coms-embed-actions">
            <button
              className="coms-btn-small"
              onClick={loadThreads}
              disabled={loading}
              title="Refresh threads"
            >
              🔄
            </button>
          </div>
        </div>

        {error && (
          <div className="coms-error-banner">{error}</div>
        )}

        {loading && (
          <div className="coms-loading">Loading threads...</div>
        )}

        {!loading && threads.length === 0 && (
          <div className="coms-empty">No threads yet</div>
        )}

        {!loading && threads.length > 0 && (
          <div className="coms-thread-list coms-thread-list--compact">
            {threads.slice(0, 5).map((thread, index) => (
              <div
                key={getEntityId(thread) || `${thread.subject || 'thread'}-${channel}-${index}`}
                className="coms-thread-item coms-thread-item--compact"
                onClick={() => handleThreadClick(thread)}
              >
                <div className="coms-thread-subject">{thread.subject}</div>
                <div className="coms-thread-meta">
                  {thread.messageCount} messages
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="coms-embed-footer">
          <button
            className="coms-btn coms-btn-primary coms-btn-small"
            onClick={handleCreateThreadClick}
          >
            + New Message
          </button>
          {deepLink && (
            <button
              className="coms-btn coms-btn-secondary coms-btn-small"
              onClick={handleOpenInComSapp}
            >
              Open in ComSapp →
            </button>
          )}
        </div>

        {!appearsLinked && (
          <div className="coms-relink-banner">
            <span>{linkingStatusDisplay}</span>
            <button
              className="coms-btn-small"
              onClick={handleRelink}
              disabled={relinkingLoading}
            >
              {relinkingLoading ? 'Relinking...' : 'Relink Account'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Full view (account page)
  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="coms-embed-panel coms-embed-panel--full">
      <div className="coms-embed-header">
        <div>
          <h3>Communication Hub</h3>
          <p className="coms-linking-status">{linkingStatusDisplay}</p>
        </div>
        <div className="coms-embed-actions">
          {view !== 'threads' && (
            <button
              className="coms-btn-secondary coms-btn-small"
              onClick={() => setView('threads')}
            >
              ← Back to threads
            </button>
          )}
          <button
            className="coms-btn-small"
            onClick={loadThreads}
            disabled={loading}
            title="Refresh"
          >
            🔄
          </button>
        </div>
      </div>

      {error && (
        <div className="coms-error-banner">{error}</div>
      )}

      {/* Threads List View */}
      {view === 'threads' && (
        <>
          {loading && <div className="coms-loading">Loading threads...</div>}

          {!loading && threads.length === 0 && (
            <div className="coms-empty">
              <p>No threads yet. Start a discussion!</p>
            </div>
          )}

          {!loading && threads.length > 0 && (
            <div className="coms-thread-list">
              {threads.map((thread, index) => (
                <div
                  key={getEntityId(thread) || `${thread.subject || 'thread'}-${channel}-${index}`}
                  className="coms-thread-item"
                  onClick={() => handleThreadClick(thread)}
                >
                  <div className="coms-thread-content">
                    <h4 className="coms-thread-subject">{thread.subject}</h4>
                    <p className="coms-thread-type">Type: {thread.type}</p>
                  </div>
                  <div className="coms-thread-meta">
                    <span className="coms-thread-count">
                      {thread.messageCount} messages
                    </span>
                    {thread.lastMessageAt && (
                      <span className="coms-thread-date">
                        {new Date(thread.lastMessageAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="coms-embed-footer">
            <button
              className="coms-btn coms-btn-primary"
              onClick={handleCreateThreadClick}
            >
              + Start New Thread
            </button>
            {deepLink && (
              <button
                className="coms-btn coms-btn-secondary"
                onClick={handleOpenInComSapp}
              >
                Open Full Space in ComSapp →
              </button>
            )}
            {!appearsLinked && (
              <button
                className="coms-btn coms-btn-warning"
                onClick={handleRelink}
                disabled={relinkingLoading}
              >
                {relinkingLoading ? 'Relinking...' : 'Relink ComSapp Account'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Thread Detail View */}
      {view === 'thread-detail' && selectedThread && (
        <>
          <div className="coms-thread-detail">
            <h4>{selectedThread.subject}</h4>
            <p className="coms-thread-type">Type: {selectedThread.type}</p>
          </div>

          {messagesLoading && (
            <div className="coms-loading">Loading messages...</div>
          )}

          {!messagesLoading && messages.length === 0 && (
            <div className="coms-empty">No messages yet</div>
          )}

          {!messagesLoading && messages.length > 0 && (
            <div className="coms-messages-list">
              {messages.map((msg, index) => (
                <div key={getEntityId(msg) || `${msg.createdAt || 'msg'}-${msg.sender?.name || 'unknown'}-${index}`} className="coms-message">
                  <div className="coms-message-header">
                    <strong>{msg.sender?.name || 'Unknown'}</strong>
                    {msg.createdAt && (
                      <span className="coms-message-date">
                        {new Date(msg.createdAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="coms-message-body">{msg.body}</div>
                  {String(msg.sender?.id || msg.sender?._id || '') === currentUserId && (
                    <button
                      type="button"
                      className="coms-msg-delete-btn"
                      onClick={() => handleDeleteMessage(getEntityId(msg))}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handlePostReply} className="coms-reply-form">
            <textarea
              className="coms-input-textarea"
              placeholder="Write your reply..."
              value={composeMessage}
              onChange={(e) => setComposeMessage(e.target.value)}
              rows={3}
              disabled={composing}
            />
            <button
              type="submit"
              className="coms-btn coms-btn-primary"
              disabled={composing || !composeMessage.trim()}
            >
              {composing ? 'Posting...' : 'Post Reply'}
            </button>
          </form>
        </>
      )}

      {/* Compose New Thread View */}
      {view === 'compose' && (
        <>
          <form onSubmit={handleCreateThread} className="coms-compose-form">
            <div className="coms-form-group">
              <label htmlFor="compose-subject">Thread Subject</label>
              <input
                id="compose-subject"
                type="text"
                className="coms-input-text"
                placeholder="Enter thread subject..."
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                disabled={composing}
              />
            </div>

            <div className="coms-form-group">
              <label htmlFor="compose-message">Message</label>
              <textarea
                id="compose-message"
                className="coms-input-textarea"
                placeholder="Write your message..."
                value={composeMessage}
                onChange={(e) => setComposeMessage(e.target.value)}
                rows={6}
                disabled={composing}
              />
            </div>

            <div className="coms-form-actions">
              <button
                type="submit"
                className="coms-btn coms-btn-primary"
                disabled={composing || !composeSubject.trim() || !composeMessage.trim()}
              >
                {composing ? 'Creating...' : 'Create Thread'}
              </button>
              <button
                type="button"
                className="coms-btn coms-btn-secondary"
                onClick={() => setView('threads')}
                disabled={composing}
              >
                Cancel
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default ComsEmbedPanel;
